import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../imagens/newlogo.png';
import './index.css';
import { QRCodeSVG } from 'qrcode.react';

// ==================== UTILITÁRIOS PIX ====================

const formatarCampoPix = (id, valor) => {
  const tamanho = valor.length.toString().padStart(2, '0');
  return `${id}${tamanho}${valor}`;
};

const calcularCRC16 = (payload) => {
  let polinomio = 0x1021;
  let resultado = 0xffff;

  for (let i = 0; i < payload.length; i++) {
    resultado ^= payload.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((resultado & 0x8000) !== 0) {
        resultado = (resultado << 1) ^ polinomio;
      } else {
        resultado <<= 1;
      }
      resultado &= 0xffff;
    }
  }
  return resultado.toString(16).toUpperCase().padStart(4, '0');
};

// Remove acentos e caracteres especiais (obrigatório no padrão EMV)
const limparTexto = (texto = '') =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .toUpperCase();

const gerarPixCopiaCola = ({ chave, nome, cidade, valor, txid = '***' }) => {
  const nomeLimpo = limparTexto(nome).substring(0, 25);
  const cidadeLimpa = limparTexto(cidade).substring(0, 15);
  const txidLimpo = limparTexto(txid).substring(0, 25) || '***';

  const merchantAccount =
    formatarCampoPix('00', 'br.gov.bcb.pix') + formatarCampoPix('01', chave);

  let payload =
    formatarCampoPix('00', '01') +
    formatarCampoPix('26', merchantAccount) +
    formatarCampoPix('52', '0000') +
    formatarCampoPix('53', '986') +
    (valor ? formatarCampoPix('54', Number(valor).toFixed(2)) : '') +
    formatarCampoPix('58', 'BR') +
    formatarCampoPix('59', nomeLimpo) +
    formatarCampoPix('60', cidadeLimpa) +
    formatarCampoPix('62', formatarCampoPix('05', txidLimpo));

  payload += '6304';

  const crc = calcularCRC16(payload);
  return payload + crc;
};

// ==================== COMPONENTE ====================

const PIX_CONFIG = {
  chave: process.env.REACT_APP_PIX_CHAVE, 
  nomeRecebedor: process.env.REACT_APP_PIX_NOME,
  cidadeRecebedor: 'CURITIBA',
};

const Portal = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [cnpj, setCnpj] = useState('');
  const [cliente, setCliente] = useState(null);
  const [financeiro, setFinanceiro] = useState([]);
  const [loadingFin, setLoadingFin] = useState(false);
  const [erro, setErro] = useState('');

  // Modal PIX
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixCodigo, setPixCodigo] = useState('');
  const [pixItemAtual, setPixItemAtual] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const credentials = btoa(`${process.env.REACT_APP_EMPRESA};${process.env.REACT_APP_LOGIN}:${process.env.REACT_APP_SENHA}`);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const res = await fetch(`https://api.wensystem.com.br/cliente/WenAPP/vendedor/CLI_ID`, {
          headers: {
            'Content-Type': 'application/json',
            'empresa': process.env.REACT_APP_EMPRESA,
            'Authorization': `Basic ${credentials}`,
          },
        });
        const text = (await res.text()).replace(/\u00A0/g, ' ').trim();
        setClientes(JSON.parse(text));
      } catch (err) {
        console.error('Erro ao carregar clientes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientes();
  }, [credentials]);

  const soNumeros = (v) => v.replace(/\D/g, '');

  const buscarPorCnpj = async () => {
    setErro('');
    setCliente(null);
    setFinanceiro([]);

    const alvo = soNumeros(cnpj);
    if (alvo.length < 11) {
      setErro('Digite um CNPJ/CPF válido.');
      return;
    }

    const encontrado = clientes.find((c) => soNumeros(c.colCnpjCpf || '') === alvo);

    if (!encontrado) {
      setErro('❌ Cliente não encontrado.');
      return;
    }

    setCliente(encontrado);
    setLoadingFin(true);

    try {
      const res = await fetch(`https://api.wensystem.com.br/receber/cliente/${encontrado.cliId}`, {
        headers: {
          'Content-Type': 'application/json',
          empresa: process.env.REACT_APP_EMPRESA,
          Authorization: `Basic ${credentials}`,
        },
      });
      const text = (await res.text()).replace(/\u00A0/g, ' ').trim();
      setFinanceiro(JSON.parse(text));
    } catch (err) {
      console.error('Erro ao buscar financeiro:', err);
    } finally {
      setLoadingFin(false);
    }
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '';
    return new Date(dataISO).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const imprimirBoleto = async (creId) => {
    try {
      await fetch(`https://api.wensystem.com.br/receber/boleto/impressao/${creId}`, {
        method: 'GET',
        headers: { empresa: process.env.REACT_APP_EMPRESA },
      });

      const pdfUrl = `https://api.wensystem.com.br/pdf/wensystem/${creId}.pdf`;
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      const agora = new Date();
      const dataHora = `${agora.getDate().toString().padStart(2, '0')}-${(agora.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${agora.getFullYear()}-${agora.getHours().toString().padStart(2, '0')}-${agora
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
      a.download = `Boleto-${cliente?.colFantasia || 'cliente'}-${dataHora}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Erro ao imprimir boleto');
    }
  };

  const abrirPix = (item) => {
    const txid = `${item.creTitulo || ''}${item.creParcela || ''}`.replace(/\D/g, '') || '***';

    const codigo = gerarPixCopiaCola({
      chave: PIX_CONFIG.chave,
      nome: PIX_CONFIG.nomeRecebedor,
      cidade: PIX_CONFIG.cidadeRecebedor,
      valor: item.creVlrReceber,
      txid,
    });

    setPixItemAtual(item);
    setPixCodigo(codigo);
    setCopiado(false);
    setShowPixModal(true);
  };

  const copiarPix = async () => {
    try {
      await navigator.clipboard.writeText(pixCodigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const fecharPixModal = () => {
    setShowPixModal(false);
    setPixCodigo('');
    setPixItemAtual(null);
  };

  const imprimirLista = () => {
    window.print();
  };

  const sair = () => {
    setCliente(null);
    setFinanceiro([]);
    setCnpj('');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <img src={logo} alt="Logo" className="loading-logo" />
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="portal-container">
      <header className="portal-header">
        <img src={logo} alt="Logo" className="portal-logo" />
        <h1>Portal do Cliente Wen System</h1>
      </header>

      <div className="portal-busca">
        <input
          type="text"
          placeholder="Digite seu CNPJ ou CPF"
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && buscarPorCnpj()}
        />
        <button onClick={buscarPorCnpj}>Consultar</button>
      </div>

      {erro && <p className="portal-erro">{erro}</p>}

      {cliente && (
        <div className="portal-dados">
          <h2>{cliente.colNome}</h2>
          <p><strong>Fantasia:</strong> {cliente.colFantasia}</p>
          <p><strong>CNPJ/CPF:</strong> {cliente.colCnpjCpf}</p>
          <p><strong>Cidade:</strong> {cliente.endCidade} - {cliente.endEstado}</p>

          <div className="portal-acoes">
            <button onClick={imprimirLista}>🖨️ Imprimir Lista</button>
            <button onClick={sair}>🚪 Sair</button>
          </div>

          <h3>Financeiro</h3>

          {loadingFin && <p>Carregando títulos...</p>}

          {!loadingFin && financeiro.length > 0 ? (
            <table className="financeiro-grid">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Título</th>
                  <th>Parcela</th>
                  <th>Pagamento</th>
                  <th>Emissão</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>Boleto</th>
                  <th>Banco</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {financeiro.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.nfePedido}</td>
                    <td>{item.creTitulo}</td>
                    <td>{item.creParcela}</td>
                    <td>{item.crePagamento}</td>
                    <td>{formatarData(item.creDtaEmissao)}</td>
                    <td>{formatarData(item.creDtaVencimento)}</td>
                    <td>{item.creStatus}</td>
                    <td>R$ {item.creVlrReceber.toFixed(2)}</td>
                    <td>{item.creNumBoleto}</td>
                    <td>{item.banNome}</td>
                    <td>
                      {item.crePagamento === 'BOLETO' ? (
                        <button className="impBoleto" onClick={() => imprimirBoleto(item.creId)}>
                          🧾 Boleto
                        </button>
                      ) : (
                        item.creStatus !== 'PAGO' && (
                          <button className="btnPix" onClick={() => abrirPix(item)}>
                            💠 Gerar PIX
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : !loadingFin && (
            <p>❌ Nenhum título encontrado</p>
          )}
        </div>
      )}

      {/* Modal PIX */}
      {showPixModal && (
        <div className="modal-overlay">
          <div className="pix-modal-content">
            <button className="pix-close-btn" onClick={fecharPixModal}>✕</button>

            <div className="pix-modal-header">
              <span className="pix-icon">💠</span>
              <h2>Pagamento via PIX</h2>
            </div>

            {pixItemAtual && (
              <div className="pix-info">
                <div className="pix-info-row">
                  <span className="pix-label">Título</span>
                  <span className="pix-value">{pixItemAtual.creTitulo} / {pixItemAtual.creParcela}</span>
                </div>
                <div className="pix-info-row pix-valor-destaque">
                  <span className="pix-label">Valor</span>
                  <span className="pix-value">R$ {pixItemAtual.creVlrReceber.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="pix-qrcode-wrapper">
              <QRCodeSVG value={pixCodigo} size={200} level="M" includeMargin={true} />
            </div>

            <p className="pix-instrucao">Escaneie o QR Code com o app do seu banco</p>

            <div className="pix-divider">
              <span>ou</span>
            </div>

            <div className="pix-codigo-box">
              <textarea readOnly value={pixCodigo} rows={3} />
            </div>

            <button className={`btn-copiar-pix ${copiado ? 'copiado' : ''}`} onClick={copiarPix}>
              {copiado ? '✅ Código copiado!' : '📋 Copiar código PIX'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portal;